import { axiosInstance } from '.';
import {
  type Merchant,
  type GetUserMerchantsResponse,
} from '../types/merchant';

function getResponseStatus(error: unknown): number | null {
  if (!error || typeof error !== 'object' || !('response' in error))
    return null;
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== 'object' || !('status' in response))
    return null;
  const status = (response as { status: number }).status;
  return typeof status === 'number' ? status : null;
}

/**
 * Retry utility with exponential backoff
 * @param fn - Function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param delayMs - Initial delay in milliseconds (default: 1000)
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  delayMs = 1000,
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry on 401/403 (authentication/authorization errors)
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) {
        throw error;
      }

      // If this isn't the last attempt, wait before retrying
      if (attempt < maxRetries - 1) {
        await new Promise((resolve) =>
          setTimeout(resolve, delayMs * Math.pow(2, attempt)),
        );
      }
    }
  }

  throw lastError;
}

/**
 * Fetches all merchants for the authenticated user.
 * Endpoint: GET /v2/user-merchants/user/merchants
 *
 * User is identified automatically via the Authorization token in request headers.
 * Returns all merchants - filtering should be done by the consumer if needed.
 *
 * Includes automatic retry logic (3 attempts with exponential backoff).
 * Does NOT retry on 401/403 errors (authentication/authorization issues).
 *
 * @param signal - Optional AbortSignal for cancellation
 * @throws Error if API call fails after retries
 */
export async function getUserMerchants(
  signal?: AbortSignal,
): Promise<Merchant[]> {
  // Fetch with retry logic
  const res = await retryWithBackoff(
    () =>
      axiosInstance.get<GetUserMerchantsResponse>(
        'v2/user-merchants/user/merchants',
        { signal },
      ),
    3, // maxRetries
    1000, // initial delay (1s, 2s, 4s for subsequent retries)
  );

  return Array.isArray(res.data) ? res.data : [];
}
